import React from 'react';
import PropTypes from 'prop-types';

/*
 * Dropdown with a checkbox per option, sized and styled like the form's other
 * selects (Category, Service). Used for Additional Services wherever a service
 * is booked or quoted: Slot Booking, Edit Booking, Quotation, Edit Quotation
 * and Edit Workstation.
 *
 * Ids for tests/automation: `${id}-toggle` on the button, `${id}-opt-${value}`
 * on each checkbox.
 */
const MultiSelectDropdown = ({
  id,
  options,
  selected,
  onChange,
  placeholder,
  emptyText,
  disabled,
  loading,
}) => {
  const [open, setOpen] = React.useState(false);
  const rootRef = React.useRef(null);
  const selectedSet = new Set((selected || []).map(String));
  const chosen = (options || []).filter((option) => selectedSet.has(String(option.value)));

  React.useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  React.useEffect(() => {
    if (disabled) setOpen(false);
  }, [disabled]);

  const toggleValue = (value) => {
    const key = String(value);
    onChange(selectedSet.has(key) ? [...selectedSet].filter((v) => v !== key) : [...selectedSet, key]);
  };

  const allChosen = options.length > 0 && chosen.length === options.length;
  const summary = chosen.length ? chosen.map((option) => option.label).join(', ') : placeholder;

  return (
    <div className={`multi-select ${open ? 'is-open' : ''}`} ref={rootRef}>
      <button
        type="button"
        id={`${id}-toggle`}
        className="form-select multi-select-toggle"
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((value) => !value)}
      >
        <span className={`multi-select-summary ${chosen.length ? '' : 'is-placeholder'}`}>{summary}</span>
        {chosen.length > 0 && <span className="multi-select-count">{chosen.length}</span>}
      </button>
      {open && (
        <div className="multi-select-menu" role="listbox" aria-multiselectable="true" aria-labelledby={`${id}-toggle`}>
          {loading && <div className="multi-select-empty">Loading…</div>}
          {!loading && options.length === 0 && <div className="multi-select-empty">{emptyText}</div>}
          {!loading && options.length > 1 && (
            <div className="multi-select-actions">
              <button type="button" onClick={() => onChange(allChosen ? [] : options.map((o) => String(o.value)))}>
                {allChosen ? 'Clear all' : 'Select all'}
              </button>
              <span>{chosen.length} of {options.length} selected</span>
            </div>
          )}
          {!loading &&
            options.map((option) => {
              const key = String(option.value);
              const checked = selectedSet.has(key);
              return (
                <label key={key} htmlFor={`${id}-opt-${key}`} className={`multi-select-option ${checked ? 'is-checked' : ''}`}>
                  <input
                    id={`${id}-opt-${key}`}
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleValue(key)}
                  />
                  <span className="multi-select-label">{option.label}</span>
                  {option.meta ? <span className="multi-select-meta">{option.meta}</span> : null}
                </label>
              );
            })}
        </div>
      )}
    </div>
  );
};

MultiSelectDropdown.propTypes = {
  id: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
      meta: PropTypes.string,
    }),
  ),
  selected: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  emptyText: PropTypes.string,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
};

MultiSelectDropdown.defaultProps = {
  options: [],
  selected: [],
  placeholder: 'Select',
  emptyText: 'No options',
  disabled: false,
  loading: false,
};

export default MultiSelectDropdown;
